import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, Globe, CheckCircle, Circle } from 'lucide-react'
import { api } from '../services/api'
import { useLanguageStore } from '../stores/languageStore'

export default function LanguageDropdown({
  currentLocale,
  availableLocales = [],
}) {
  const [isOpen, setIsOpen] = useState(false)
  const { setCurrentLanguage, initializeLanguage, _hasHydrated } =
    useLanguageStore()

  const {
    data: languagesRes,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['languages'],
    queryFn: () => api.get('/languages').then((r) => r.data),
  })

  const languages = Array.isArray(languagesRes?.data)
    ? languagesRes.data
    : Array.isArray(languagesRes)
    ? languagesRes
    : []
  const currentLanguage = languages.find((lang) => lang.code === currentLocale)

  useEffect(() => {
    if (_hasHydrated && languages.length > 0 && !currentLocale) {
      initializeLanguage(languages)
    }
  }, [languages, currentLocale, initializeLanguage, _hasHydrated])

  const handleLanguageSelect = (language) => {
    setCurrentLanguage(language.id, language.code)
    setIsOpen(false)
  }

  if (!_hasHydrated) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-500">
        <Globe className="h-4 w-4" />
        <span>Initializing...</span>
      </div>
    )
  }

  if (!currentLocale) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-500">
        <Globe className="h-4 w-4" />
        <span>Loading...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 text-sm text-red-500">
        <Globe className="h-4 w-4" />
        <span>Error</span>
      </div>
    )
  }

  if (isLoading || isFetching || languages.length === 0) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-500">
        <Globe className="h-4 w-4" />
        <span>Loading...</span>
      </div>
    )
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
      >
        <Globe className="h-4 w-4" />
        <span>{currentLanguage?.flag || '🌐'}</span>
        <span className="font-medium">
          {currentLanguage?.name || currentLocale}
        </span>
        <ChevronDown className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-1 w-56 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
            {languages.map((language) => {
              const hasTranslation = availableLocales.includes(language.code)
              return (
                <button
                  key={language.code}
                  onClick={() => handleLanguageSelect(language)}
                  className={`flex w-full items-center space-x-3 px-4 py-2 text-sm transition-colors ${
                    language.code === currentLocale
                      ? 'bg-gray-100 text-gray-900'
                      : hasTranslation
                      ? 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                  }`}
                >
                  {hasTranslation ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Circle className="h-4 w-4 text-gray-300" />
                  )}
                  <span className="text-lg">{language.flag || '🌐'}</span>
                  <div className="flex flex-col items-start flex-1">
                    <span className="font-medium">{language.name}</span>
                    <span className="text-xs text-gray-500">
                      {language.native_name}
                    </span>
                  </div>
                  {language.is_default && (
                    <span className="text-xs text-blue-600 font-medium">
                      Default
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
